import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import * as actionCreators from "../actions";
import downloadViewStateToProps from "../selectors";

import DownloadLayout from "./DownloadLayout";

const DownloadView = props => {
  const { location, boundActions: actions } = props;

  useEffect(() => {
    document.title = "CTD Download";
    actions.updateSurveyStationOptions();
    actions.downLoadDeviceInfoFetch();
    if (location.search !== "?" && location.search !== "") {
      actions.updateFiltersFromQuery(location.search);
    }
  }, []); // dependencies are empty so this runs only on mount.

  const handleFindCastsClick = () => {
    actions.downloadCastsInvalidate();
    actions.setTotalCastsWithFilters(0);
    actions.fetchTotalCastsWithFilters();
    actions.downloadCastsFetchIfNeeded();
  };

  const handleWorkareaFilterChipChange = chips => {
    actions.downloadCastsInvalidate();
    actions.setTotalCastsWithFilters(0);
    actions.setWorkareaFilter(chips);
  };

  const handleSurveyFilterChipChange = chips => {
    actions.downloadCastsInvalidate();
    actions.setTotalCastsWithFilters(0);
    actions.setSurveyFilter(chips);
  };

  const handleStationFilterChipChange = chips => {
    actions.downloadCastsInvalidate();
    actions.setTotalCastsWithFilters(0);
    actions.setStationFilter(chips);
  };

  const handleFilenameFilterChipChange = chips => {
    actions.setFilenameFilter(chips);
  };

  const handleHakaiIdFilterChipChange = chips => {
    actions.setHakaiIdFilter(chips);
  };

  const handleCastPkFilterChipChange = chips => {
    if (chips.every(chip => !Number.isNaN(parseInt(chip, 10))))
      actions.setCastPkFilter(chips);
  };

  const handleResetButtonClick = () => {
    actions.setStartDateFilter("");
    actions.setEndDateFilter("");
    actions.setWorkareaFilter([]);
    actions.setSurveyFilter([]);
    actions.setStationFilter([]);
  };

  const handleDownloadTypeSelect = value => {
    const [downloadType, processingStage] = value.split("/");
    actions.downloadCastsInvalidate();
    actions.setTotalCastsWithFilters(0);
    actions.setProcessingStageFilter(processingStage);
    actions.setDownloadType(downloadType);
    // Try to help users out by setting instrument to seabird if they want processed cnv files
    // Can still change instrument type back, just won't be able to return any requests for rbr files
    if (value === "file/8_binAvg") {
      actions.setInstrumentFilterOverride("SBE19plus, SBE25, SBE25plus");
    } else {
      actions.setInstrumentFilterOverride("");
    }
  };

  const handleDownloadAllClick = type => {
    switch (type) {
      case "csv":
        actions.csvDownloadWithFilters();
        break;
      case "xlsx":
        actions.xlsxDownloadWithFilters();
        break;
      // "file" (file/1_datCnv, file/8_binAvg) and the default "hexrsk"
      // (hexrsk/raw) both bundle the original files into a zip.
      case "file":
      default:
        actions.zipDownloadWithFilters();
    }
  };

  const handleDownloadMetadataClick = type => {
    switch (type) {
      case "csv":
        actions.csvDownloadMetadataWithFilters();
        break;
      case "xlsx":
      default:
        actions.xlsxDownloadMetadataWithFilters();
    }
  };

  const handleViewClick = (type, cast) => {
    switch (type) {
      case "csv":
        actions.viewCsv(cast);
        break;
      case "file":
        actions.viewFile(cast);
        break;
      default:
    }
  };

  const handleDownloadClick = (type, cast) => {
    switch (type) {
      case "csv":
        actions.downloadCsv(cast);
        break;
      case "xlsx":
        actions.downloadXlsx(cast);
        break;
      case "file":
        actions.downloadFile(cast);
        break;
      case "hexrsk":
        actions.downloadHexRsk(cast);
        break;
      default:
    }
  };

  const handleFAQClick = () => {
    actions.openFAQModal();
  };

  const handleFAQClose = () => {
    actions.closeFAQModal();
  };

  const handleCreateCastDistChart = () => {
    actions.castDistChartDataFetch();
    actions.openCastDistChartModal();
  };

  const handleCastDistChartModalClose = () => {
    actions.closeCastDistChartModal();
  };

  const handleSetBrushExtent = extent => {
    actions.setBrushExtent(extent);
  };

  const handleClearBrush = extent => {
    actions.setBrushExtent(extent);
    actions.clearBrush();
  };

  const handleBrushDidClear = () => {
    actions.brushDidClear();
  };

  return (
    <DownloadLayout
      {...props}
      onCloseViewFileDialog={() => actions.closePreviewFileDialog()}
      onStartDateFilterChange={e => {
        actions.downloadCastsInvalidate();
        actions.setTotalCastsWithFilters(0);
        actions.setStartDateFilter(e);
      }}
      onEndDateFilterChange={e => {
        actions.downloadCastsInvalidate();
        actions.setTotalCastsWithFilters(0);
        actions.setEndDateFilter(e);
      }}
      onWorkareaFilterChipChange={chip => handleWorkareaFilterChipChange(chip)}
      onSurveyFilterChipChange={chip => handleSurveyFilterChipChange(chip)}
      onStationFilterChipChange={chip => handleStationFilterChipChange(chip)}
      onDownloadTypeSelect={e => handleDownloadTypeSelect(e.value)}
      onInstrumentFilterChange={e =>
        actions.setInstrumentFilter(e.target.value)
      }
      onSerialNumFilterChange={e => actions.setSerialNumFilter(e.target.value)}
      onOrganizationFilterChange={e => actions.setOrganizationFilter(e)}
      onBufferFilterChange={bool => actions.setBufferFilter(bool)}
      onUpcastFilterChange={bool => actions.setUpcastFilter(bool)}
      onFilenameFilterChipChange={chips =>
        handleFilenameFilterChipChange(chips)
      }
      onCastPkFilterChipChange={chips => handleCastPkFilterChipChange(chips)}
      onHakaiIdFilterChipChange={chips => handleHakaiIdFilterChipChange(chips)}
      onItemsPerPageChange={e => actions.setItemsPerPage(e.target.value)}
      onFindCastsClick={() => handleFindCastsClick()}
      onDownloadAllClick={type => handleDownloadAllClick(type)}
      onDownloadMetadataClick={type => handleDownloadMetadataClick(type)}
      onViewClick={(type, cast) => handleViewClick(type, cast)}
      onDownloadClick={(type, cast) => handleDownloadClick(type, cast)}
      onDownloadPDFClick={cast => actions.downloadPDF(cast)}
      onNextPageClick={() => actions.nextPage()}
      onPrevPageClick={() => actions.prevPage()}
      onFAQClick={() => handleFAQClick()}
      onFAQClose={() => handleFAQClose()}
      onCreateCastDistChartClick={() => handleCreateCastDistChart()}
      onCastDistChartModalClose={() => handleCastDistChartModalClose()}
      onSetBrushExtent={extent => handleSetBrushExtent(extent)}
      onClearBrush={extent => handleClearBrush(extent)}
      brushDidClear={() => handleBrushDidClear()}
      onResetButtonClick={handleResetButtonClick}
    />
  );
};

DownloadView.propTypes = {
  surveyFilter: PropTypes.arrayOf(PropTypes.string),
  stationFilter: PropTypes.arrayOf(PropTypes.string),
  filenameFilter: PropTypes.arrayOf(PropTypes.string),
  castPkFilter: PropTypes.arrayOf(PropTypes.string),
  hakaiIdFilter: PropTypes.arrayOf(PropTypes.string),
  brushDidInvalidate: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired
  }).isRequired,
  boundActions: PropTypes.shape({
    updateSurveyStationOptions: PropTypes.func.isRequired,
    downloadCastsInvalidate: PropTypes.func.isRequired,
    setTotalCastsWithFilters: PropTypes.func.isRequired,
    fetchTotalCastsWithFilters: PropTypes.func.isRequired,
    downloadCastsFetchIfNeeded: PropTypes.func.isRequired,
    setSurveyFilter: PropTypes.func.isRequired,
    setStationFilter: PropTypes.func.isRequired,
    closePreviewFileDialog: PropTypes.func.isRequired,
    setStartDateFilter: PropTypes.func.isRequired,
    setEndDateFilter: PropTypes.func.isRequired,
    setDownloadType: PropTypes.func.isRequired,
    setProcessingStageFilter: PropTypes.func.isRequired,
    setInstrumentFilter: PropTypes.func.isRequired,
    setInstrumentFilterOverride: PropTypes.func.isRequired,
    setSerialNumFilter: PropTypes.func.isRequired,
    setWorkareaFilter: PropTypes.func.isRequired,
    setOrganizationFilter: PropTypes.func.isRequired,
    setBufferFilter: PropTypes.func.isRequired,
    setUpcastFilter: PropTypes.func.isRequired,
    setFilenameFilter: PropTypes.func.isRequired,
    setCastPkFilter: PropTypes.func.isRequired,
    setHakaiIdFilter: PropTypes.func.isRequired,
    xlsxDownloadWithFilters: PropTypes.func.isRequired,
    csvDownloadWithFilters: PropTypes.func.isRequired,
    zipDownloadWithFilters: PropTypes.func.isRequired,
    xlsxDownloadMetadataWithFilters: PropTypes.func.isRequired,
    csvDownloadMetadataWithFilters: PropTypes.func.isRequired,
    viewCsv: PropTypes.func.isRequired,
    viewFile: PropTypes.func.isRequired,
    downloadCsv: PropTypes.func.isRequired,
    downloadXlsx: PropTypes.func.isRequired,
    downloadFile: PropTypes.func.isRequired,
    downloadHexRsk: PropTypes.func.isRequired,
    downloadPDF: PropTypes.func.isRequired,
    nextPage: PropTypes.func.isRequired,
    prevPage: PropTypes.func.isRequired,
    setItemsPerPage: PropTypes.func.isRequired,
    openFAQModal: PropTypes.func.isRequired,
    closeFAQModal: PropTypes.func.isRequired,
    openCastDistChartModal: PropTypes.func.isRequired,
    closeCastDistChartModal: PropTypes.func.isRequired,
    castDistChartDataFetch: PropTypes.func.isRequired,
    setBrushExtent: PropTypes.func.isRequired,
    brushDidClear: PropTypes.func.isRequired,
    clearBrush: PropTypes.func.isRequired,
    updateFiltersFromQuery: PropTypes.func.isRequired,
    downLoadDeviceInfoFetch: PropTypes.isRequired
  }).isRequired
};

DownloadView.defaultProps = {
  surveyFilter: [],
  stationFilter: [],
  filenameFilter: [],
  castPkFilter: [],
  hakaiIdFilter: []
};

const mapDispatchToProps = dispatch => ({
  boundActions: bindActionCreators(actionCreators, dispatch)
});

export default withRouter(
  connect(downloadViewStateToProps, mapDispatchToProps)(DownloadView)
);
